import type { ClassificationPathNode, CsiSystem, CsiTerm } from "design-scope";
import { classificationNumberSearchValues } from "./classification-code-spacing";
import { toApiClassificationSystem } from "./design-scope-list-filter";

export const CLASSIFICATION_SEARCH_LIMIT = 50;
export const ANCESTRY_MAX_DEPTH = 12;

export interface GqlClassificationItem {
  id: string | number;
  number: string | null;
  title: string | null;
  system: string;
  version?: number | null;
  parent_id: string | number | null;
}

/** Existing custom classification from `classification_items` (title-only). */
export type CustomClassificationHit = {
  id: string;
  title: string;
};

/** Catalog PKs may be Int or uuid; pass numbers when the id is all digits. */
export function toGraphqlClassificationId(id: string): string | number {
  return /^\d+$/.test(id) ? Number(id) : id;
}

function escapeIlike(raw: string): string {
  return raw.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
}

function toUiSystem(system: string): CsiSystem | null {
  if (system === "master-format" || system === "MF") return "MF";
  if (system === "uni-format" || system === "UF") return "UF";
  return null;
}

export function mapGqlClassificationItem(
  row: GqlClassificationItem
): CsiTerm | null {
  const system = toUiSystem(row.system);
  if (!system) return null;
  const number = (row.number ?? "").trim();
  const title = (row.title ?? "").trim();
  if (!number && !title) return null;
  return {
    id: String(row.id),
    system,
    number,
    title,
    parentId: row.parent_id == null ? null : String(row.parent_id),
    titleLower: title.toLowerCase(),
    numberAndTitle: [number, title].filter(Boolean).join(" - "),
    parentNumber: null,
    depth: 1,
    search: `${number} ${title}`.toLowerCase()
  };
}

/** Map a custom `classification_items` row (drops empty titles). */
export function mapGqlCustomClassificationItem(
  row: GqlClassificationItem
): CustomClassificationHit | null {
  const system = (row.system ?? "").toLowerCase();
  if (system !== "custom") return null;
  const title = (row.title ?? "").trim();
  if (!title) return null;
  return { id: String(row.id), title };
}

export function buildClassificationSearchVariables(
  query: string,
  system: CsiSystem
): { where: Record<string, unknown>; limit: number } | null {
  const q = query.trim();
  if (!q) return null;
  const pattern = `%${escapeIlike(q)}%`;
  const numberPatterns = classificationNumberSearchValues(q, system).map(
    (value) => `%${escapeIlike(value)}%`
  );
  return {
    where: {
      _and: [
        { system: { _eq: toApiClassificationSystem(system) } },
        {
          _or: [
            { title: { _ilike: pattern } },
            ...numberPatterns.map((numberPattern) => ({
              number: { _ilike: numberPattern }
            }))
          ]
        }
      ]
    },
    limit: CLASSIFICATION_SEARCH_LIMIT
  };
}

/** Title-only search against custom classification_items. */
export function buildCustomClassificationSearchVariables(
  query: string
): { where: Record<string, unknown>; limit: number } | null {
  const q = query.trim();
  if (!q) return null;
  const pattern = `%${escapeIlike(q)}%`;
  return {
    where: {
      _and: [{ system: { _eq: "custom" } }, { title: { _ilike: pattern } }]
    },
    limit: CLASSIFICATION_SEARCH_LIMIT
  };
}

export function buildClassificationByIdsVariables(ids: string[]): {
  where: Record<string, unknown>;
} | null {
  const unique = [...new Set(ids.filter(Boolean))];
  if (!unique.length) return null;
  return { where: { id: { _in: unique.map(toGraphqlClassificationId) } } };
}

/**
 * Root-first path for a picked term, walking `parent_id` until a root
 * or the depth/cycle bound. `fetchByIds` is the page's lazy GraphQL call.
 */
export async function walkClassificationAncestry(
  term: CsiTerm,
  fetchByIds: (ids: string[]) => Promise<CsiTerm[]>
): Promise<ClassificationPathNode[]> {
  const chain: ClassificationPathNode[] = [
    { number: term.number, title: term.title }
  ];
  const seen = new Set<string>([term.id]);
  let parentId = term.parentId ?? null;
  let depth = 0;
  while (parentId && depth < ANCESTRY_MAX_DEPTH) {
    if (seen.has(parentId)) break;
    seen.add(parentId);
    const rows = await fetchByIds([parentId]);
    const parent = rows[0];
    if (!parent) break;
    chain.unshift({ number: parent.number, title: parent.title });
    parentId = parent.parentId ?? null;
    depth += 1;
  }
  return chain;
}
